import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  LocationOn as LocationIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Analytics as AnalyticsIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';

interface SavedProperty {
  id: string;
  property_id: string;
  address: string;
  notes: string | null;
  tags: string[] | null;
  created_at: string;
  property_details: {
    lot_size: number;
    building_area: number;
    year_built: number;
    zoning_code: string;
    assessed_value: number;
  } | null;
}

interface UserStats {
  total_searches: number;
  total_saved_properties: number;
  total_reports_generated: number;
  recent_activity: {
    searches: Array<{ address: string; date: string }>;
    saved: Array<{ property_id: string; date: string }>;
  };
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [savedProperties, setSavedProperties] = useState<SavedProperty[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [propertiesRes, statsRes] = await Promise.allSettled([
        axios.get('/api/users/properties?limit=6'),
        axios.get('/api/users/usage-stats'),
      ]);
      
      // Handle properties response
      if (propertiesRes.status === 'fulfilled') {
        setSavedProperties(propertiesRes.value.data);
      } else {
        console.error('Failed to fetch properties:', propertiesRes.reason);
        setSavedProperties([]);
      }
      
      // Handle stats response
      if (statsRes.status === 'fulfilled') {
        setUserStats(statsRes.value.data);
      } else {
        console.error('Failed to fetch stats:', statsRes.reason);
        // Set default stats for new user
        setUserStats({
          total_searches: 0,
          total_saved_properties: 0,
          total_reports_generated: 0,
          recent_activity: {
            searches: [],
            saved: []
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Set defaults if everything fails
      setSavedProperties([]);
      setUserStats({
        total_searches: 0,
        total_saved_properties: 0,
        total_reports_generated: 0,
        recent_activity: {
          searches: [],
          saved: []
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveProperty = async (propertyId: string) => {
    try {
      await axios.delete(`/api/users/properties/${propertyId}`);
      setSavedProperties(prev => prev.filter(p => p.property_id !== propertyId));
      if (userStats) {
        setUserStats({
          ...userStats,
          total_saved_properties: userStats.total_saved_properties - 1,
        });
      }
    } catch (error) {
      console.error('Failed to remove property:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                Welcome back, {user?.full_name || user?.username}!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Here's an overview of your property analysis activity
              </Typography>
            </Box>
            {(user as any)?.is_admin && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AdminIcon />}
                onClick={() => navigate('/admin/dashboard')}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Admin Dashboard
              </Button>
            )}
          </Box>
        </Box>
      </motion.div>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {loading ? (
          [1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))
        ) : (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Card 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: theme => theme.shadows[4],
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                      <SearchIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight={600}>
                      {userStats?.total_searches || 0}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Properties Analyzed
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Card 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: theme => theme.shadows[4],
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ bgcolor: 'success.light', mr: 2 }}>
                      <HomeIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight={600}>
                      {userStats?.total_saved_properties || 0}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Saved Properties
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <Card 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: theme => theme.shadows[4],
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ bgcolor: 'warning.light', mr: 2 }}>
                      <AssessmentIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight={600}>
                      {userStats?.total_reports_generated || 0}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Reports Generated
                  </Typography>
                </Card>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.4 }}
              >
                <Card 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.main',
                      boxShadow: theme => theme.shadows[4],
                      transform: 'translateY(-2px)',
                    }
                  }}
                  onClick={() => navigate('/profile')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Avatar sx={{ bgcolor: 'info.light', mr: 2 }}>
                      <AnalyticsIcon />
                    </Avatar>
                    <Typography variant="h4" fontWeight={600}>
                      Profile
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Account Status: Pro
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </>
        )}
      </Grid>

      {/* Quick Actions */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          mb: 4, 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Quick Actions
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/app')}
            sx={{ textTransform: 'none' }}
          >
            New Analysis
          </Button>
          <Button
            variant="outlined"
            startIcon={<ViewIcon />}
            onClick={() => navigate('/properties')}
            sx={{ textTransform: 'none' }}
          >
            View All Properties
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => navigate('/reports')}
            sx={{ textTransform: 'none' }}
          >
            View Reports
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Saved Properties */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 400,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Saved Properties
              </Typography>
              <Button
                size="small"
                onClick={() => navigate('/properties')}
                sx={{ textTransform: 'none' }}
              >
                View All
              </Button>
            </Box>

            {loading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={100} sx={{ mb: 2 }} />
                ))}
              </Box>
            ) : savedProperties.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" gutterBottom>
                  No saved properties yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/app')}
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Analyze Your First Property
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {savedProperties.map((property) => (
                  <Grid size={{ xs: 12, md: 6 }} key={property.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                          <LocationIcon color="action" sx={{ mr: 1, mt: 0.5 }} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1" fontWeight={500}>
                              {property.address}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Saved {formatDate(property.created_at)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {property.property_details && (
                          <Box sx={{ mt: 2 }}>
                            <Grid container spacing={1}>
                              <Grid size={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Lot Size
                                </Typography>
                                <Typography variant="body2">
                                  {property.property_details.lot_size.toLocaleString()} sq ft
                                </Typography>
                              </Grid>
                              <Grid size={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Assessed Value
                                </Typography>
                                <Typography variant="body2">
                                  {formatCurrency(property.property_details.assessed_value)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                        
                        {property.tags && property.tags.length > 0 && (
                          <Box sx={{ mt: 2, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {property.tags.map((tag, index) => (
                              <Chip key={index} label={tag} size="small" />
                            ))}
                          </Box>
                        )}
                      </CardContent>
                      <CardActions>
                        <Button
                          size="small"
                          onClick={() => navigate(`/app?address=${encodeURIComponent(property.address)}`)}
                        >
                          View Analysis
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProperty(property.property_id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 400,
            }}
          >
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Recent Activity
            </Typography>
            
            {loading ? (
              <Box>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} variant="text" height={40} />
                ))}
              </Box>
            ) : userStats?.recent_activity.searches.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No recent activity
              </Typography>
            ) : (
              <List dense>
                {userStats?.recent_activity.searches.slice(0, 5).map((search, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <SearchIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={search.address}
                      secondary={formatDate(search.date)}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;